import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TradingActionBoard from './TradingActionBoard';
import V1PipelineStrip from './V1PipelineStrip';
import V1ChartApprovePanel from './V1ChartApprovePanel';
import ServerErrorBanner, { useServerErrors } from './ServerErrorBanner';
import { formatIstDateTime } from './ist-datetime';
import IstClock from './IstClock';
import { DAILY_POOL_CAP, LIVE_SLOT_COUNT, PER_SHARE_PROFIT, HOLD_PROFIT_TARGET_RS, HOLD_ESCAPE_MIN_RS, HOLD_ESCAPE_MAX_RS, HOLD_SOFT_AFTER_MIN, HOLD_STOP_AFTER_MIN, SELL_TAB_AUTO_SOLD_SEC, fmtRsPerShare, UNLIMITED_CAPITAL, MIN_SHARE_PRICE, MAX_SHARE_PRICE, attachHoldTiming, attachSellTiming, isTimedOut, qualifiesHoldTabSell, MAX_HOLD_MINUTES, STOCK_QTY, applyV1MasterSettings, BUY_STICKY_UNTIL_PROFIT } from './trading-rules';
import {
  armSellAudioAutoUnlock,
  fireSellAlert,
  playMovingToHoldVoice,
  playSellBuzz,
  playSoldDoneChime,
  playStopLossSoldVoice,
  requestSellNotifications,
  warmSellAudio
} from './sell-alert-audio';
import './NormalTrading.css';
import './NormalTradingV1.css';

const API_TIMEOUT_MS = 28000;
const LIVE_POLL_MS = 1000;
const PRICE_POLL_MS = 1000;
const SOLD_TODAY_POLL_MS = 10000;
const FAIL_BEFORE_ALERT = 5;

function boardHasContent(b) {
  if (!b) return false;
  const al = b.actionLists;
  if (!al) return Boolean(b.v1Pipeline || b.lastAction);
  return Boolean(
    (al.buy && al.buy.length)
    || (al.hold && al.hold.length)
    || (al.sell && al.sell.length)
    || (al.marketPlusRsToday && al.marketPlusRsToday.length)
    || b.v1Pipeline
  );
}

/** Keep last good pipeline/positions when API returns warming-up empty shell. */
function mergeLiveBoard(prev, next) {
  if (!next) return prev;
  if (!prev) return next;
  if (boardHasContent(next) && !next.boardError) return next;

  const prevAl = prev.actionLists || {};
  const nextAl = next.actionLists || {};
  const pickList = (key) => (
    (nextAl[key] && nextAl[key].length) ? nextAl[key] : (prevAl[key] || [])
  );

  return {
    ...next,
    v1Pipeline: next.v1Pipeline || prev.v1Pipeline || null,
    v1PipelineFilter: next.v1PipelineFilter || prev.v1PipelineFilter || null,
    currentPositions: (next.currentPositions && next.currentPositions.length)
      ? next.currentPositions
      : (prev.currentPositions || []),
    slotFillPreview: (next.slotFillPreview && next.slotFillPreview.length)
      ? next.slotFillPreview
      : (prev.slotFillPreview || []),
    actionLists: {
      ...prevAl,
      ...nextAl,
      buy: pickList('buy'),
      hold: pickList('hold'),
      sell: pickList('sell'),
      marketPlusRsToday: pickList('marketPlusRsToday'),
      bestPerformersToday: pickList('bestPerformersToday'),
      soldToday: mergeSoldTodayLists(prevAl.soldToday, nextAl.soldToday),
      stopLossToday: mergeSoldTodayLists(prevAl.stopLossToday, nextAl.stopLossToday),
      shortToday: mergeSoldTodayLists(prevAl.shortToday, nextAl.shortToday)
    },
    marketPlusRsMeta: (next.marketPlusRsMeta?.count > 0)
      ? next.marketPlusRsMeta
      : (prev.marketPlusRsMeta || next.marketPlusRsMeta),
    usOvernightCue: (next.usOvernightCue?.ok || next.usOvernightCue?.indices?.length)
      ? next.usOvernightCue
      : (prev.usOvernightCue || next.usOvernightCue),
    basket: next.basket || prev.basket,
    capital: next.capital || prev.capital,
    profitPlan: next.profitPlan || prev.profitPlan,
    dayCatchMonitor: next.dayCatchMonitor || prev.dayCatchMonitor,
    buyTabHint: next.buyTabHint || prev.buyTabHint,
    stale: true,
    boardError: next.boardError || null
  };
}

function mergeSoldTodayLists(prev = [], next = []) {
  const map = new Map();
  const keyOf = (r) => [
    String(r?.stock || r?.symbol || '').replace(/\.NS$/i, ''),
    r?.sellTimeLabel || r?.time || '',
    Number((r?.sellPrice ?? r?.price) || 0).toFixed(2),
    r?.qty || 0
  ].join('|');
  for (const r of [...(prev || []), ...(next || [])]) {
    if (!r?.stock && !r?.symbol) continue;
    const k = keyOf(r);
    const cur = map.get(k);
    const t = Number(r?.lastUpdated) || Date.parse(r?.savedAt || r?.sellDateTime || '') || 0;
    const ct = Number(cur?.lastUpdated) || Date.parse(cur?.savedAt || cur?.sellDateTime || '') || 0;
    if (!cur || t >= ct) map.set(k, r);
  }
  return [...map.values()].sort((a, b) => {
    const ta = Number(a?.lastUpdated) || Date.parse(a?.savedAt || a?.sellDateTime || '') || 0;
    const tb = Number(b?.lastUpdated) || Date.parse(b?.savedAt || b?.sellDateTime || '') || 0;
    return tb - ta;
  });
}

function quotesToPriceMap(data) {
  const map = {};
  const rows = data?.quotes || [];
  for (const q of rows) {
    const key = (q.stock || q.symbol || '').replace(/\.NS$/i, '');
    if (!key || !q.price) continue;
    map[key] = { ...q, stock: key, fetchedAt: q.fetchedAt || Date.now() };
  }
  if (data?.prices && typeof data.prices === 'object') {
    for (const [key, q] of Object.entries(data.prices)) {
      if (q?.price) map[key.replace(/\.NS$/i, '')] = q;
    }
  }
  return map;
}

function isShortSide(item) {
  const side = String(item?.side || item?.positionSide || '').toLowerCase();
  return side === 'short'
    || item?.shareTag === 'short'
    || item?.chartAiShort === true
    || item?.direction === 'DOWN';
}

/** Per-share economic move: LONG = LTP−entry · SHORT = entry−LTP (Rule 3 from original entry). */
function perShareMoveFromPrices(entry, livePx, short) {
  if (!(Number(entry) > 0) || !(Number(livePx) > 0)) return null;
  return short
    ? Math.round((Number(entry) - Number(livePx)) * 100) / 100
    : Math.round((Number(livePx) - Number(entry)) * 100) / 100;
}

function patchItemPrices(item, priceMap) {
  if (!item?.stock) return item;
  // Keep SELL LTP live — P&L from original entry (Rule 3); SHORT inverted
  const q = priceMap[item.stock];
  if (!q?.price) return item;
  const currentPrice = q.price;
  const bid = q.bid ?? q.sellBrokerPrice ?? currentPrice;
  const ask = q.ask ?? q.buyBrokerPrice ?? currentPrice;
  const entry = Number(item.botPrice ?? item.entryPrice ?? item.price);
  const validEntry = Number.isFinite(entry) && entry > 0;
  const short = isShortSide(item);
  const move = validEntry
    ? perShareMoveFromPrices(entry, currentPrice, short)
    : item.move;
  // SHORT cover uses ask (buy-to-cover); LONG sell uses bid
  const brokerPx = short ? ask : bid;
  const brokerSellMove = validEntry && brokerPx != null
    ? perShareMoveFromPrices(entry, brokerPx, short)
    : null;
  return {
    ...item,
    currentPrice,
    bid,
    ask,
    buyBrokerPrice: ask,
    sellBrokerPrice: bid,
    volume: q.volume ?? item.volume,
    bidSize: q.bidSize ?? item.bidSize,
    askSize: q.askSize ?? item.askSize,
    move,
    perShareMove: move,
    brokerSellMove,
    side: short ? 'short' : (item.side || 'long'),
    positionSide: short ? 'short' : (item.positionSide || 'long'),
    priceFrozen: false,
    live: true,
    priceFetchedAt: q.fetchedAt || Date.now()
  };
}

function liveStatusLabel(st, marketOpen, totalDay = 0, profitTarget = 100) {
  void profitTarget;
  if (marketOpen === true && st !== 'disabled') {
    return { cls: 'running', text: totalDay > 0 ? 'LIVE · EARN MORE' : 'LIVE' };
  }
  if (st === 'running' || st === 'waiting' || st === 'target_hit') {
    return { cls: 'running', text: 'LIVE · EARN MORE' };
  }
  if (st === 'market_closed' || st === 'weekend') return { cls: 'weekend', text: 'MARKET CLOSED' };
  return { cls: 'running', text: 'LIVE · EARN MORE' };
}

function itemOpenedAt(item) {
  if (item?.openedAt) return item.openedAt;
  if (item?.previewOpenedAt) return new Date(item.previewOpenedAt).toISOString();
  return null;
}

function shouldMoveToHold(item, move) {
  if (BUY_STICKY_UNTIL_PROFIT) return false;
  const openedAt = itemOpenedAt(item);
  if (!openedAt) return false;
  if (move != null && move >= PER_SHARE_PROFIT - 0.004) return false;
  return isTimedOut({ openedAt }, MAX_HOLD_MINUTES);
}

function buyTimedOutWeakOrPartial(item, move) {
  return shouldMoveToHold(item, move);
}

function itemHoldSince(item) {
  return item?.holdSince || itemOpenedAt(item);
}

function classifyLiveTab(item, priceMap, sellAlertsOpen = true) {
  const patched = patchItemPrices(item, priceMap);
  const entry = Number(patched.botPrice ?? patched.entryPrice ?? patched.price);
  const validEntry = Number.isFinite(entry) && entry > 0;
  const move = validEntry
    ? (patched.move ?? (
      patched.currentPrice != null
        ? Math.round((Number(patched.currentPrice) - entry) * 100) / 100
        : null
    ))
    : null;
  const withMove = move != null ? { ...patched, move, perShareMove: move } : patched;
  const openedAt = itemOpenedAt(withMove);
  const holdSince = itemHoldSince(withMove);
  const withOpen = openedAt ? { ...withMove, openedAt, holdSince } : withMove;

  const coverLabel = isShortSide(patched) ? 'COVER' : 'SELL';
  const asSell = (row, { urgent = false, reason } = {}) => ({
    tab: 'sell',
    item: {
      ...row,
      kind: urgent ? 'urgent' : 'rotate',
      liveSellAlert: sellAlertsOpen,
      sellAlertsMuted: !sellAlertsOpen,
      workflowTab: 'sell',
      reason: reason || (sellAlertsOpen
        ? `+₹${row.move?.toFixed(2)}/sh — ${coverLabel} on Groww`
        : 'After 3:15 PM IST — sell tab (alerts muted until 9:17 AM)')
    }
  });

  const asHold = (row, reason) => ({
    tab: 'hold',
    item: {
      ...row,
      workflowTab: 'hold',
      holdSince: row.holdSince || new Date().toISOString(),
      reason: reason || `10 min — no ${fmtRsPerShare(PER_SHARE_PROFIT)}/sh — HOLD (Rule 2)`
    }
  });

  // Ghost pending with entry ₹0 must not fake +LTP/sh onto SELL
  if (!validEntry) {
    return { tab: patched.workflowTab === 'hold' ? 'hold' : 'buy', item: withOpen };
  }

  // SELL latch is one-way — never reclassify back to BUY/HOLD (any LTP / any condition)
  if (patched.sellTabSince || patched.workflowTab === 'sell') {
    return asSell(withOpen, {
      urgent: true,
      reason: withOpen.reason || `${coverLabel} tab — timer → Sold Today`
    });
  }

  // Groww mirror picks (pending)
  if (patched.pending) {
    if (move != null && move >= PER_SHARE_PROFIT - 0.004) {
      return asSell(withOpen, {
        urgent: true,
        reason: `+₹${move.toFixed(2)}/sh ≥ ${fmtRsPerShare(PER_SHARE_PROFIT)}/sh — ${coverLabel} on Groww now`
      });
    }
    if (shouldMoveToHold(withOpen, move)) {
      return asHold(withOpen);
    }
    return { tab: 'buy', item: withOpen };
  }

  const wfTab = patched.workflowTab || 'buy';

  if (wfTab === 'buy' && move != null && move >= PER_SHARE_PROFIT - 0.004) {
    return asSell(withOpen, {
      urgent: true,
      reason: `+₹${move.toFixed(2)}/sh ≥ ${fmtRsPerShare(PER_SHARE_PROFIT)}/sh — ${coverLabel} on Groww now`
    });
  }
  if (wfTab === 'buy' && shouldMoveToHold(withOpen, move)) {
    return asHold(withOpen);
  }
  const holdSellOpts = {
    profitRs: withOpen.holdProfitRs ?? HOLD_PROFIT_TARGET_RS,
    escapeMin: withOpen.holdEscapeMinRs ?? HOLD_ESCAPE_MIN_RS,
    escapeMax: withOpen.holdEscapeMaxRs ?? HOLD_ESCAPE_MAX_RS,
    softAfterMin: withOpen.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN,
    stopAfterMin: withOpen.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN
  };
  if (wfTab === 'hold' && move != null && qualifiesHoldTabSell(move, holdSince, Date.now(), holdSellOpts)) {
    const pt = holdSellOpts.profitRs;
    return asSell(withOpen, {
      urgent: true,
      reason: move >= pt - 0.004
        ? `HOLD → +₹${move.toFixed(2)}/sh (≥${fmtRsPerShare(pt)}/sh Master) — ${coverLabel} tab`
        : `HOLD → +₹${move.toFixed(2)}/sh (Master soft/late rule) — ${coverLabel} tab`
    });
  }
  if (wfTab === 'hold') return { tab: 'hold', item: withOpen };
  return { tab: 'buy', item: withOpen };
}

function buildActionLists(lists, priceMap, nowMs = Date.now(), sellAlertsOpen = true) {
  const buy = [];
  const hold = [];
  const sell = [];
  const seen = new Set();

  const ingest = (item) => {
    const key = item.stock || item.symbol;
    if (!key || seen.has(key)) return;
    seen.add(key);
    const { tab, item: row } = classifyLiveTab(item, priceMap, sellAlertsOpen);
    if (tab === 'sell') {
      sell.push(attachSellTiming({
        ...row,
        holdSoftAfterMin: row.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN,
        holdStopAfterMin: row.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN,
        sellWaitSec: row.sellWaitSec ?? SELL_TAB_AUTO_SOLD_SEC
      }, nowMs));
    } else if (tab === 'hold') {
      hold.push(attachHoldTiming({
        ...row,
        holdSoftAfterMin: row.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN,
        holdStopAfterMin: row.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN
      }, 'hold', nowMs));
    } else {
      buy.push(attachHoldTiming(row, 'buy', nowMs));
    }
  };

  for (const item of lists.sell || []) ingest(item);
  for (const item of lists.hold || []) ingest(item);
  for (const item of lists.buy || []) ingest(item);

  const holdSortMs = (row) => (
    Date.parse(row?.holdSince || row?.openedAt || '')
    || Number(row?.lastUpdated)
    || 0
  );
  hold.sort((a, b) => holdSortMs(b) - holdSortMs(a));

  return { buy, hold, sell };
}

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function NormalTradingV1() {
  const [status, setStatus] = useState(null);
  const [soldTodayList, setSoldTodayList] = useState([]);
  const [stopLossTodayList, setStopLossTodayList] = useState([]);
  const [soldTodayRefreshing, setSoldTodayRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState('connecting');
  const [livePrices, setLivePrices] = useState({ status: 'idle', prices: {}, prevPrices: {}, priceSource: null });
  const [holdClock, setHoldClock] = useState(() => Date.now());
  const [controlsOpen, setControlsOpen] = useState(() =>
    typeof window !== 'undefined' ? !window.matchMedia('(max-width: 900px)').matches : true
  );
  const { errors: serverErrors, pushError, clearSource, dismiss: dismissServerError, clearAll: clearAllServerErrors } = useServerErrors();

  const enabledRef = useRef(false);
  const boardInFlightRef = useRef(false);
  const pricesInFlightRef = useRef(false);
  const failRef = useRef({ board: 0, prices: 0, health: 0 });
  const lastBoardRef = useRef(null);
  const sellUrgentBeepedRef = useRef(new Set());
  const holdAnnouncedRef = useRef(new Set());
  const stopLossAnnouncedRef = useRef(new Set());
  const sellBuzzIntervalRef = useRef(null);
  const soldTodayCountRef = useRef(0);
  const holdWarmRef = useRef(false);
  const liveStreamRef = useRef(null);

  const soldTodayInFlightRef = useRef(false);
  const soldTodayPendingRef = useRef(false);

  const loadSoldToday = useCallback(async () => {
    if (soldTodayInFlightRef.current) {
      soldTodayPendingRef.current = true;
      return;
    }
    soldTodayInFlightRef.current = true;
    setSoldTodayRefreshing(true);
    try {
      const res = await axios.get('/api/normal-v1/sold-history', { timeout: 15000 });
      const rows = res.data?.soldToday || [];
      const list = Array.isArray(rows) ? rows : [];
      setSoldTodayList((prev) => mergeSoldTodayLists(prev, list));
      const stops = res.data?.stopLossToday;
      if (Array.isArray(stops)) {
        setStopLossTodayList((prev) => mergeSoldTodayLists(prev, stops));
      } else {
        const stopRe = /stop[- ]?loss|hold stop|net stop|\/sh net|−₹|from buy|LTP\s*[−\-].*%/i;
        setStopLossTodayList((prev) => mergeSoldTodayLists(
          prev,
          list.filter((r) => r?.stopLoss || r?.stopLossTotalRs != null || stopRe.test(String(r?.reason || '')))
        ));
      }
    } catch {
      /* keep last good sold-today list */
    } finally {
      soldTodayInFlightRef.current = false;
      setSoldTodayRefreshing(false);
      if (soldTodayPendingRef.current) {
        soldTodayPendingRef.current = false;
        setTimeout(() => {
          loadSoldToday();
        }, 100);
      }
    }
  }, []);

  const applyBoard = useCallback((data) => {
    if (!data) return;
    if (data.v1Master) applyV1MasterSettings(data.v1Master);
    const merged = mergeLiveBoard(lastBoardRef.current, data);
    lastBoardRef.current = merged;
    setStatus(merged);
    // Keep Sold Today in sync — merge board + local so new sells never get stuck behind stale state
    const boardSold = merged?.actionLists?.soldToday;
    if (Array.isArray(boardSold)) {
      setSoldTodayList((prev) => mergeSoldTodayLists(prev, boardSold));
    }
    const boardStops = merged?.actionLists?.stopLossToday;
    if (Array.isArray(boardStops)) {
      setStopLossTodayList((prev) => mergeSoldTodayLists(prev, boardStops));
    }
    if (boardHasContent(merged)) {
      setConnection(merged.stale ? 'stale' : 'ok');
    } else if (merged.stale) {
      setConnection('stale');
    } else {
      setConnection('ok');
    }
  }, []);

  const loadLiveBoard = useCallback(async (forceFresh = false) => {
    if (boardInFlightRef.current) return lastBoardRef.current;
    boardInFlightRef.current = true;
    try {
      const res = await axios.get('/api/normal-v1/live-board', {
        timeout: API_TIMEOUT_MS,
        params: forceFresh ? { warm: 1 } : undefined
      });
      applyBoard(res.data);
      failRef.current.board = 0;
      if (boardHasContent(res.data)) {
        clearSource('v1-live-board');
        clearSource('bootstrap');
      }
      if (res.data.liveConnection?.ok === false && failRef.current.prices >= FAIL_BEFORE_ALERT) {
        pushError('live-connection', `Live quotes slow (${res.data.liveConnection.failures || 0} failures) — bot still running`);
      } else {
        clearSource('live-connection');
      }
      return res.data;
    } catch (e) {
      failRef.current.board += 1;
      setConnection(lastBoardRef.current ? 'stale' : 'reconnecting');
      if (failRef.current.board >= FAIL_BEFORE_ALERT) {
        pushError('v1-live-board', e);
      }
      return lastBoardRef.current;
    } finally {
      boardInFlightRef.current = false;
    }
  }, [applyBoard, clearSource, pushError]);

  const refreshLivePrices = useCallback(async (forceFresh = false) => {
    if (pricesInFlightRef.current) return;
    pricesInFlightRef.current = true;
    try {
      const res = await axios.get('/api/normal/live-prices', {
        timeout: 10000,
        params: forceFresh ? { fresh: 1 } : undefined
      });
      const map = quotesToPriceMap(res.data);
      const hasQuotes = Object.keys(map).length > 0;
      setLivePrices({
        status: hasQuotes ? (res.data?.stale ? 'stale' : 'live') : 'reconnecting',
        prices: map,
        prevPrices: res.data.prevPrices || {},
        priceSource: res.data.priceSource || res.data.source || (hasQuotes ? 'yahoo_live' : null)
      });
      if (hasQuotes) {
        failRef.current.prices = 0;
        clearSource('live-prices');
        clearSource('bootstrap');
      } else {
        failRef.current.prices += 1;
        if (failRef.current.prices >= FAIL_BEFORE_ALERT) {
          pushError('live-prices', 'Live prices empty — retrying');
        }
      }
    } catch (e) {
      failRef.current.prices += 1;
      setLivePrices((prev) => ({ ...prev, status: 'reconnecting' }));
      if (failRef.current.prices >= FAIL_BEFORE_ALERT) {
        pushError('live-prices', e);
      }
    } finally {
      pricesInFlightRef.current = false;
    }
  }, [clearSource, pushError]);

  const applyLiveStreamPayload = useCallback((data) => {
    const map = quotesToPriceMap(data);
    const hasQuotes = Object.keys(map).length > 0;
    if (!hasQuotes) return;
    setLivePrices({
      status: data?.stale ? 'stale' : 'live',
      prices: map,
      prevPrices: data?.prevPrices || {},
      priceSource: data?.priceSource || data?.source || 'yahoo_live'
    });
    failRef.current.prices = 0;
    clearSource('live-prices');
    clearSource('bootstrap');
  }, [clearSource]);

  const pingHealth = useCallback(async () => {
    try {
      await axios.get('/api/normal-v1/health', { timeout: 5000 });
      failRef.current.health = 0;
      clearSource('health');
    } catch (e) {
      failRef.current.health += 1;
      if (failRef.current.health >= FAIL_BEFORE_ALERT) {
        pushError('health', e);
      }
    }
  }, [clearSource, pushError]);

  const bootstrapLive = useCallback(async () => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const board = await loadLiveBoard(attempt === 0);
      await refreshLivePrices(true);
      if (boardHasContent(board)) {
        clearSource('bootstrap');
        clearSource('v1-live-board');
        return;
      }
      await sleep(1500 * (attempt + 1));
    }
    pushError('bootstrap', 'Could not reach live board after retries — keep this tab open, auto-retrying');
  }, [loadLiveBoard, refreshLivePrices, clearSource, pushError]);

  const refreshBoard = useCallback(async () => {
    await warmSellAudio();
    setBusy(true);
    try {
      await Promise.all([loadLiveBoard(true), refreshLivePrices(true), loadSoldToday(), pingHealth()]);
      clearSource('refresh');
    } finally {
      setBusy(false);
    }
  }, [loadLiveBoard, refreshLivePrices, loadSoldToday, pingHealth, clearSource]);

  const resetBuyHold = useCallback(async () => {
    if (!window.confirm('Clear BUY & HOLD tabs and start fresh monitoring?\n\nSold today will NOT be removed.')) return;
    setBusy(true);
    try {
      const res = await axios.post('/api/normal-v1/reset-buy-hold', {}, { timeout: API_TIMEOUT_MS });
      if (res.data?.message) clearSource('reset-buy-hold');
      await Promise.all([loadLiveBoard(true), refreshLivePrices(true)]);
    } catch (e) {
      pushError('reset-buy-hold', e.response?.data?.message || e.message || 'Reset failed');
    } finally {
      setBusy(false);
    }
  }, [loadLiveBoard, refreshLivePrices, clearSource, pushError]);

  const setBuyConfirmMode = useCallback(async (mode) => {
    const labels = {
      'candle-confirm': 'Roll back to candle confirm only?\n\nFee-aware (RS if Nifty weak + volume + hard net) will turn OFF.\nCandle UP filter stays ON.',
      'fee-aware-momentum': 'Enable fee-aware momentum?\n\nEinstein → candle UP → (if Nifty down: stock must lead) → volume → net≥min → BUY'
    };
    if (!window.confirm(labels[mode] || `Switch BUY mode to ${mode}?`)) return;
    setBusy(true);
    try {
      await axios.post('/api/normal-v1/buy-confirm-mode', { mode }, { timeout: API_TIMEOUT_MS });
      clearSource('buy-confirm-mode');
      await loadLiveBoard(true);
    } catch (e) {
      pushError('buy-confirm-mode', e.response?.data?.message || e.message || 'Strategy switch failed');
    } finally {
      setBusy(false);
    }
  }, [loadLiveBoard, clearSource, pushError]);

  const buyConfirmMode = status?.v1Pipeline?.buyConfirmMode
    || (status?.v1Master?.feeAwareMomentum === false
      ? (status?.v1Master?.candleConfirmBuy === false ? 'quote-only' : 'candle-confirm')
      : 'fee-aware-momentum');

  useEffect(() => {
    try {
      localStorage.removeItem('normal-trading-v1-mode');
    } catch {
      /* ignore */
    }
    document.body.classList.add('nt-body');
    return () => document.body.classList.remove('nt-body');
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const sync = () => {
      if (!mq.matches) setControlsOpen(true);
    };
    mq.addEventListener?.('change', sync);
    mq.addListener?.(sync);
    return () => {
      mq.removeEventListener?.('change', sync);
      mq.removeListener?.(sync);
    };
  }, []);

  useEffect(() => {
    if (!enabledRef.current) {
      enabledRef.current = true;
      axios.post('/api/normal/enable', {}, { timeout: 8000 }).catch((e) => {
        if (failRef.current.board >= FAIL_BEFORE_ALERT) pushError('enable', e);
      });
    }
    let cancelled = false;
    bootstrapLive();
    loadSoldToday();
    const boardTimer = setInterval(() => loadLiveBoard(false), LIVE_POLL_MS);
    const priceTimer = setInterval(() => refreshLivePrices(false), PRICE_POLL_MS);
    const soldTodayTimer = setInterval(loadSoldToday, SOLD_TODAY_POLL_MS);
    const healthTimer = setInterval(pingHealth, 15000);

    const connectStream = () => {
      if (liveStreamRef.current) {
        try { liveStreamRef.current.close(); } catch { /* ignore */ }
      }
      const es = new EventSource('/api/normal/live-stream');
      liveStreamRef.current = es;
      es.onmessage = (ev) => {
        try {
          applyLiveStreamPayload(JSON.parse(ev.data));
        } catch { /* ignore */ }
      };
      es.onerror = () => {
        setLivePrices((prev) => ({ ...prev, status: 'reconnecting' }));
        try { es.close(); } catch { /* ignore */ }
        liveStreamRef.current = null;
        if (!cancelled) setTimeout(connectStream, 3000);
      };
    };
    connectStream();
    refreshLivePrices(true);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadLiveBoard(false);
        refreshLivePrices(true);
        loadSoldToday();
        if (!liveStreamRef.current) connectStream();
      }
    };
    const onOnline = () => {
      connectStream();
      loadLiveBoard(false);
      refreshLivePrices(true);
      loadSoldToday();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      clearInterval(boardTimer);
      clearInterval(priceTimer);
      clearInterval(soldTodayTimer);
      clearInterval(healthTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      if (liveStreamRef.current) {
        try { liveStreamRef.current.close(); } catch { /* ignore */ }
        liveStreamRef.current = null;
      }
    };
  }, [bootstrapLive, loadLiveBoard, loadSoldToday, refreshLivePrices, pingHealth, pushError, applyLiveStreamPayload]);

  useEffect(() => {
    const timer = setInterval(() => setHoldClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const lists = status?.actionLists || {};
  const priceMap = livePrices.prices || {};
  const sellAlertsOpen = status?.sellAlertsOpen !== false;

  const { buy: buyList, hold: holdList, sell: sellList } = useMemo(
    () => buildActionLists(lists, priceMap, holdClock, sellAlertsOpen),
    [lists, priceMap, holdClock, sellAlertsOpen]
  );
  const bestPerformersList = useMemo(() => {
    const rows = (lists.bestPerformersToday || []).map((item) => patchItemPrices(item, priceMap));
    return [...rows].sort((a, b) => (Number(a?.rank) || 999) - (Number(b?.rank) || 999));
  }, [lists.bestPerformersToday, priceMap]);

  const stopLossList = useMemo(() => {
    const stopRe = /stop[- ]?loss|hold stop|net stop|\/sh net|−₹|from buy|LTP\s*[−\-].*%|≥\s*\d|-\d+(\.\d+)?%\s*from/i;
    const isStop = (r) => {
      const intended = !!(
        r?.stopLoss
        || r?.stopLossTotalRs != null
        || r?.stopLossPct != null
        || stopRe.test(String(r?.reason || ''))
      );
      if (!intended) return false;
      // Exit recovered to profit → Sold Today only (not Stop Loss Today)
      if (Number(r?.netPnl) > 0.004 || Number(r?.move) > 0.004 || Number(r?.pnl) > 0.004) return false;
      return true;
    };
    const fromPoll = stopLossTodayList.length ? stopLossTodayList : [];
    const fromBoard = lists.stopLossToday || [];
    const pool = (fromPoll.length ? fromPoll : (fromBoard.length ? fromBoard : (soldTodayList.length ? soldTodayList : (lists.soldToday || []))))
      .filter(isStop);
    return [...pool].sort((a, b) => {
      const ta = Number(a?.lastUpdated) || Date.parse(a?.savedAt || a?.sellDateTime || '') || 0;
      const tb = Number(b?.lastUpdated) || Date.parse(b?.savedAt || b?.sellDateTime || '') || 0;
      return tb - ta;
    });
  }, [lists.stopLossToday, lists.soldToday, soldTodayList, stopLossTodayList]);

  const shortTodayListUi = useMemo(() => {
    const fromBoard = lists.shortToday || [];
    const fromSold = (soldTodayList.length ? soldTodayList : (lists.soldToday || []))
      .filter((r) =>
        String(r?.side || r?.positionSide || '').toLowerCase() === 'short'
        || r?.shareTag === 'short'
      );
    const pool = fromBoard.length ? fromBoard : fromSold;
    return [...pool].sort((a, b) => {
      const ta = Number(a?.lastUpdated) || Date.parse(a?.savedAt || a?.sellDateTime || a?.openedAt || '') || 0;
      const tb = Number(b?.lastUpdated) || Date.parse(b?.savedAt || b?.sellDateTime || b?.openedAt || '') || 0;
      return tb - ta;
    });
  }, [lists.shortToday, lists.soldToday, soldTodayList]);

  useEffect(() => {
    requestSellNotifications();
    const disarm = armSellAudioAutoUnlock();
    warmSellAudio();
    return disarm;
  }, []);

  const sellActive = useMemo(() => sellList.filter((s) => !s.soldToday), [sellList]);
  const sellHistory = useMemo(
    () => (soldTodayList.length ? soldTodayList : (lists.soldToday || [])),
    [soldTodayList, lists.soldToday]
  );
  // Profit on SELL tab (LONG or SHORT economic +move) → beep + voice
  const sellUrgent = useMemo(() => (
    sellAlertsOpen
      ? sellActive.filter((s) => s.move == null || Number(s.move) > 0.004 || s.kind === 'urgent' || s.liveSellAlert)
      : []
  ), [sellAlertsOpen, sellActive]);

  useEffect(() => {
    if (!sellAlertsOpen) {
      sellUrgentBeepedRef.current.clear();
      if (sellBuzzIntervalRef.current) {
        clearInterval(sellBuzzIntervalRef.current);
        sellBuzzIntervalRef.current = null;
      }
      return;
    }
    for (const row of sellUrgent) {
      const sk = row.stock;
      if (!sk || sellUrgentBeepedRef.current.has(sk)) continue;
      sellUrgentBeepedRef.current.add(sk);
      fireSellAlert({ stock: sk, move: row.move, tripleBlink: true });
    }
    const activeKeys = new Set(sellUrgent.map((r) => r.stock).filter(Boolean));
    for (const sk of [...sellUrgentBeepedRef.current]) {
      if (!activeKeys.has(sk)) sellUrgentBeepedRef.current.delete(sk);
    }
  }, [sellUrgent, sellAlertsOpen]);

  // Sheet: BUY → HOLD voice ("moving to hold tab")
  useEffect(() => {
    const keys = new Set(
      (holdList || []).map((r) => r.stock).filter(Boolean)
    );
    if (!holdWarmRef.current) {
      holdWarmRef.current = true;
      holdAnnouncedRef.current = keys;
      return;
    }
    for (const sk of keys) {
      if (holdAnnouncedRef.current.has(sk)) continue;
      holdAnnouncedRef.current.add(sk);
      playMovingToHoldVoice(sk);
    }
    for (const sk of [...holdAnnouncedRef.current]) {
      if (!keys.has(sk)) holdAnnouncedRef.current.delete(sk);
    }
  }, [holdList]);

  useEffect(() => {
    const rows = stopLossList || [];
    for (const row of rows) {
      const sk = row.stock;
      const id = row.id || `${sk}:${row.soldAt || row.exitAt || row.savedAt || ''}`;
      if (!sk || stopLossAnnouncedRef.current.has(id)) continue;
      stopLossAnnouncedRef.current.add(id);
      const total = row.stopLossTotalRs ?? (row.netPnl != null ? Math.abs(Number(row.netPnl)) : null);
      playStopLossSoldVoice(sk, total);
    }
  }, [stopLossList]);

  useEffect(() => {
    // Short 3-blink → Sold Today flow: no repeating 12s buzz
    if (sellBuzzIntervalRef.current) {
      clearInterval(sellBuzzIntervalRef.current);
      sellBuzzIntervalRef.current = null;
    }
  }, [sellUrgent.length, sellAlertsOpen]);

  useEffect(() => {
    const n = sellHistory.length;
    if (n > soldTodayCountRef.current) {
      playSoldDoneChime();
      soldTodayCountRef.current = n;
    } else if (n < soldTodayCountRef.current) {
      soldTodayCountRef.current = n;
    }
  }, [sellHistory.length]);

  useEffect(() => () => {
    if (sellBuzzIntervalRef.current) clearInterval(sellBuzzIntervalRef.current);
  }, []);

  const activeStep = useMemo(() => {
    if (sellActive.length > 0) return 2;
    if (buyList.some((b) => b.stock && !b.pending)) return 1;
    if (buyList.some((b) => b.stock && b.pending)) return 1;
    return 3;
  }, [sellActive.length, buyList]);

  const v1Master = status?.v1Master;
  const profitRs = v1Master?.buyProfitRs ?? PER_SHARE_PROFIT;
  const profitLabel = fmtRsPerShare(profitRs);
  const holdProfitRs = v1Master?.holdProfitRs ?? HOLD_PROFIT_TARGET_RS;
  const holdEscapeMin = v1Master?.holdEscapeMinRs ?? HOLD_ESCAPE_MIN_RS;
  const holdEscapeMax = v1Master?.holdEscapeMaxRs ?? HOLD_ESCAPE_MAX_RS;
  const holdMinutes = v1Master?.maxHoldMinutes ?? MAX_HOLD_MINUTES;
  const stockQty = v1Master?.maxBuyQty ?? v1Master?.stockQty ?? STOCK_QTY;
  const minBuyQty = v1Master?.minBuyQty ?? 1;
  const maxBuyQty = v1Master?.maxBuyQty ?? 10;
  const slotsMax = v1Master?.slotsMax ?? LIVE_SLOT_COUNT;
  const priceMin = v1Master?.activeBand?.min ?? MIN_SHARE_PRICE;
  const priceMax = v1Master?.activeBand?.max ?? MAX_SHARE_PRICE;
  const bandLabel = v1Master?.activeBand?.label ?? 'Penny';
  const capitalAmount = v1Master?.capitalAmount ?? status?.capital?.dailyTradingCapital ?? DAILY_POOL_CAP;
  const isUnlimited = v1Master?.unlimitedCapital === true;
  const capital = status?.capital || status?.profitPlan || {};
  const capitalAvailable = Number(capital.capitalAvailable ?? capital.capitalFree ?? capital.cashFree ?? 0);
  const capitalUsed = Number(capital.capitalUsed ?? capital.capitalInUse ?? 0);
  // Capital card = Master seed only — never show open invested value here
  const capitalWallet = isUnlimited
    ? Number(capital.walletTotal ?? (capitalAvailable + capitalUsed))
    : Number(capital.dailyTradingCapital ?? capital.startingCapital ?? capitalAmount);
  const totalDay = status?.totalDayPnl ?? ((status?.dailyPnl ?? 0) + (status?.unrealizedPnl ?? 0));
  const profitTarget = status?.profitPlan?.profitTarget ?? status?.settings?.dailyTarget ?? 100;
  const livePill = liveStatusLabel(status?.status, status?.marketOpen, totalDay, profitTarget);
  const istTime = status?.istTime ? formatIstDateTime(status.istTime) : '';
  const connectionHint = connection === 'ok'
    ? null
    : connection === 'stale'
      ? 'Using last good board — refreshing…'
      : 'Connecting to server…';

  return (
    <div className="nt-desktop nt-v1 nt-no-sidebar">
      <main className="nt-main">
        <section className="nt-v1-top-split" aria-label="V1 overview and actions">
          <div className="nt-v1-top-main">
            <header className="nt-header nt-header-compact nt-v1-page-header">
              <div className="nt-v1-header-main">
                <div className="nt-v1-header-title-row">
                  <div className="nt-v1-header-titles">
                    <h1>Normal Trading Version 1</h1>
                    <p className="nt-subtitle nt-subtitle-desktop">
                      {status?.lastAction || `2-way earn · LONG up / SHORT down · Einstein · band ₹${priceMin}–₹${priceMax}`}
                    </p>
                  </div>
                  <div className="nt-v1-header-right">
                    <IstClock />
                    <div className={`nt-status-pill ${livePill.cls}`}>
                      <span className="nt-status-dot" />
                      {livePill.text}
                    </div>
                  </div>
                </div>

                <div className="nt-v1-stat-cards" role="group" aria-label="Capital summary">
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">Capital</span>
                    <strong>₹{Number(capitalWallet || capitalAmount).toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">Available</span>
                    <strong className="green">₹{Math.max(0, capitalAvailable).toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="nt-v1-stat-card" title="Open BUY+HOLD+SELL invested — hard-capped to Master Capital">
                    <span className="nt-v1-stat-label">In stocks</span>
                    <strong className={capitalUsed > capitalWallet + 0.01 ? 'red' : undefined}>
                      ₹{capitalUsed.toLocaleString('en-IN')}
                    </strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">Einstein</span>
                    <strong>100% only</strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">P&amp;L today</span>
                    <strong className={totalDay >= 0 ? 'green' : 'red'}>₹{totalDay.toFixed(0)}</strong>
                  </div>
                  <div
                    className="nt-v1-stat-card"
                    title={status?.dayCatchMonitor?.hint || 'Day catch vs ₹1000 goal'}
                  >
                    <span className="nt-v1-stat-label">
                      {status?.dayCatchMonitor?.dayLossSoft
                        ? 'Soft quality'
                        : (status?.dayCatchMonitor?.flatCrisis
                          ? 'Flat boost'
                          : (status?.dayCatchMonitor?.catchBoost ? 'Catch boost' : 'Day goal'))}
                    </span>
                    <strong className={status?.dayCatchMonitor?.dayLossSoft ? 'red' : (totalDay >= profitTarget ? 'green' : undefined)}>
                      {status?.dayCatchMonitor
                        ? `₹${Math.round(status.dayCatchMonitor.dayPnl)}/${status.dayCatchMonitor.targetRs}`
                        : `₹${Math.round(totalDay)}/${profitTarget}`}
                    </strong>
                  </div>
                  <div className="nt-v1-stat-card" title="Pipeline fills today · est need for leftover goal">
                    <span className="nt-v1-stat-label">Catch fills</span>
                    <strong>
                      {status?.dayCatchMonitor
                        ? `${status.dayCatchMonitor.fillsToday} · need ~${status.dayCatchMonitor.needFills}`
                        : '—'}
                    </strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">Slots</span>
                    <strong>{status?.basket?.slotsUsed ?? 0}/{slotsMax}</strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">Qty</span>
                    <strong>{minBuyQty}–{maxBuyQty}</strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">Band</span>
                    <strong>₹{priceMin}–₹{priceMax}</strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">BUY target</span>
                    <strong className="green">{profitLabel}/sh</strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">HOLD target</span>
                    <strong className="green">{fmtRsPerShare(holdProfitRs)}/sh</strong>
                  </div>
                  <div className="nt-v1-stat-card">
                    <span className="nt-v1-stat-label">Seed</span>
                    <strong>{isUnlimited ? 'Unlimited' : `₹${Number(capitalAmount).toLocaleString('en-IN')}`}</strong>
                  </div>
                </div>

                <nav className="nt-v1-header-links" aria-label="V1 quick links">
                  <Link to="/ledger/normal">Buy/Sell Ledger</Link>
                  <Link to="/NormalTrading/Sold">Sold Today</Link>
                  <Link to="/NormalTrading/Master">V1 Master</Link>
                </nav>
              </div>
            </header>
          </div>

          <aside className="nt-v1-top-actions" aria-label="V1 controls">
            <div className={`nt-v1-actions-panel${controlsOpen ? ' is-open' : ' is-collapsed'}`}>
              <button
                type="button"
                className="nt-v1-actions-toggle"
                onClick={() => {
                  if (!window.matchMedia('(max-width: 900px)').matches) return;
                  setControlsOpen((open) => !open);
                }}
                aria-expanded={controlsOpen}
                aria-controls="nt-v1-controls-body"
              >
                <span className="nt-v1-actions-title">Controls</span>
                <span className="nt-v1-actions-chevron" aria-hidden="true">{controlsOpen ? '▲' : '▼'}</span>
              </button>
              <div
                id="nt-v1-controls-body"
                className="nt-v1-toolbar nt-v1-toolbar-actions mobile-toolbar-row"
              >
                <button type="button" className="nt-v1-btn primary" onClick={refreshBoard} disabled={busy}>
                  {busy ? 'Refreshing…' : 'Refresh'}
                </button>
                <button type="button" className="nt-v1-btn" onClick={resetBuyHold} disabled={busy} title="Clear BUY & HOLD only — Sold today stays">
                  Reset BUY/HOLD
                </button>
                {buyConfirmMode === 'fee-aware-momentum' ? (
                  <button
                    type="button"
                    className="nt-v1-btn gold nt-v1-btn-span"
                    onClick={() => setBuyConfirmMode('candle-confirm')}
                    disabled={busy}
                    title="Turn OFF Nifty/volume/hard-net — keep candle UP confirm only"
                  >
                    Rollback to candle confirm
                  </button>
                ) : (
                  <button
                    type="button"
                    className="nt-v1-btn success nt-v1-btn-span"
                    onClick={() => setBuyConfirmMode('fee-aware-momentum')}
                    disabled={busy}
                    title="Einstein → candle UP → Nifty OK → volume → net≥min → BUY"
                  >
                    Enable fee-aware momentum
                  </button>
                )}
                <span className={`nt-v1-strategy-pill ${buyConfirmMode}`} title={status?.v1Pipeline?.strategyNote || ''}>
                  {buyConfirmMode === 'fee-aware-momentum' ? 'Fee-aware ON' : buyConfirmMode === 'candle-confirm' ? 'Candle only' : 'Quote only'}
                </span>
                <details className="nt-mobile-more-actions">
                  <summary>More</summary>
                  <button
                    type="button"
                    className="nt-v1-btn danger"
                    onClick={async () => {
                      if (!window.confirm('Fresh start today?\n\n• Clear open BUY / HOLD / SELL slots only\n• Record any open positions into Sold Today / history first\n• NEVER delete trade history or sold ledger\n• Set capital to ₹50,000 (V1 + CNC)')) return;
                      setBusy(true);
                      try {
                        const res = await axios.post('/api/normal-v1/reset-buy-hold', {}, { timeout: API_TIMEOUT_MS });
                        if (res.data?.message) clearSource('reset-buy-hold');
                        await axios.post('/api/normal-v1/master-settings', { capitalAmount: 50000, unlimitedCapital: false }, { timeout: API_TIMEOUT_MS });
                        await Promise.all([loadLiveBoard(true), refreshLivePrices(true)]);
                        try { window.location.reload(); } catch { /* ignore */ }
                      } catch (e) {
                        pushError('reset-all', e.response?.data?.message || e.message || 'Reset failed');
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    title="Clear BUY, HOLD, SELL; set capital to ₹50,000"
                  >
                    Reset screen &amp; ₹50k
                  </button>
                </details>
                <button
                  type="button"
                  className="nt-v1-btn danger nt-desktop-only"
                  onClick={async () => {
                    if (!window.confirm('Fresh start today?\n\n• Clear open BUY / HOLD / SELL slots only\n• Record any open positions into Sold Today / history first\n• NEVER delete trade history or sold ledger\n• Set capital to ₹50,000 (V1 + CNC)')) return;
                    setBusy(true);
                    try {
                      const res = await axios.post('/api/normal-v1/reset-buy-hold', {}, { timeout: API_TIMEOUT_MS });
                      if (res.data?.message) clearSource('reset-buy-hold');
                      await axios.post('/api/normal-v1/master-settings', { capitalAmount: 50000, unlimitedCapital: false }, { timeout: API_TIMEOUT_MS });
                      await Promise.all([loadLiveBoard(true), refreshLivePrices(true)]);
                      try { window.location.reload(); } catch { /* ignore */ }
                    } catch (e) {
                      pushError('reset-all', e.response?.data?.message || e.message || 'Reset failed');
                    } finally {
                      setBusy(false);
                    }
                  }}
                  disabled={busy}
                  title="Clear BUY, HOLD, SELL; set capital to ₹50,000"
                >
                  Reset screen & set ₹50,000
                </button>
              </div>
            </div>
          </aside>
        </section>

        {connectionHint && (
          <p className="nt-v1-connection-hint" role="status">{connectionHint}</p>
        )}

        <ServerErrorBanner
          errors={serverErrors}
          onDismiss={dismissServerError}
          onClearAll={clearAllServerErrors}
        />

        <div className="nt-mobile-pipeline-wrap">
          <V1PipelineStrip pipeline={status?.v1Pipeline} />
          <V1ChartApprovePanel
            chartGate={status?.v1Pipeline?.chartGate}
            priceMap={priceMap}
            subtitle="2-way earn: chart UP → LONG (profit if rises). Chart DOWN → SHORT (profit if falls). Approve → Buy tab."
            onChanged={async () => {
              await loadLiveBoard(true);
            }}
          />
        </div>
        <TradingActionBoard
          buy={buyList}
          hold={holdList}
          sell={sellList}
          sellActive={sellActive}
          sellHistory={sellHistory}
          bestPerformersToday={bestPerformersList}
          stopLossToday={stopLossList}
          shortToday={shortTodayListUi}
          sellUrgentCount={sellUrgent.length}
          sellTodayCount={sellHistory.length}
          activeStep={activeStep}
          profitLabel={profitLabel}
          profitRs={profitRs}
          holdPartialMin={holdEscapeMin}
          holdPartialMax={holdEscapeMax}
          holdMinutes={holdMinutes}
          stockQty={stockQty}
          slotsMax={slotsMax}
          volatileMode
          quantSlotCount={status?.basket?.slotsUsed ?? 0}
          liveStatus={livePrices.status}
          priceSource={livePrices.priceSource}
          strategyCompareMode={false}
          fullyAutomated
          slotsFree={status?.basket?.slotsFree}
          holdSlotCount={holdList.length}
          onRefreshSoldToday={loadSoldToday}
          soldTodayRefreshing={soldTodayRefreshing}
        />
      </main>
    </div>
  );
}
